import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ClientJobBreakdown from "@/components/ClientJobBreakdown";
import { toast } from "sonner";
import {
  ClipboardList, Send, CalendarCheck, Trophy, Users, TrendingUp, ArrowUpRight, Briefcase,
  KeyRound, Eye, EyeOff, Copy,
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="bg-[#12151F] border border-slate-800/80 rounded-xl p-5 shadow-lg shadow-black/40 hover:border-slate-700/80 transition-colors">
    <div className="flex items-center justify-between">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <div className="mt-4 font-mono font-bold text-3xl text-slate-100 tracking-tight">{value}</div>
    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-1">{label}</div>
  </div>
);

const StatusBadge = ({ decision }) => (
  <span className={decision === "APPLY"
    ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide uppercase"
    : "bg-rose-500/15 text-rose-400 border border-rose-500/30 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide uppercase"}>
    {decision}
  </span>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [clients, setClients] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/evaluations").then((r) => setRecent(r.data.slice(0, 6)));
    api.get("/clients").then((r) => setClients(r.data));
  }, []);

  useEffect(() => {
    if (user?.role === "executive" && user?.id) {
      setLoadingBreakdown(true);
      api.get(`/executives/${user.id}/breakdown`)
        .then((r) => setBreakdown(r.data.clients || []))
        .finally(() => setLoadingBreakdown(false));
    }
  }, [user?.id, user?.role]);

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-100 tracking-tight">Welcome back, {user?.name?.split(" ")[0]}</h1>
          <p className="text-slate-400 text-sm mt-1">Your job-fit command center. Here's how the pipeline looks.</p>
        </div>
        <Link to="/extension" data-testid="dashboard-open-extension" className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors">
          Evaluate a job <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-evaluated" icon={ClipboardList} label="Evaluated" value={stats?.total_evaluated ?? "—"} accent="bg-blue-500/15 text-blue-400" />
        <StatCard testid="stat-applied" icon={Send} label="Applied" value={stats?.applied ?? "—"} accent="bg-purple-500/15 text-purple-400" />
        <StatCard testid="stat-interviews" icon={CalendarCheck} label="Interviews" value={stats?.interviews ?? "—"} accent="bg-amber-500/15 text-amber-400" />
        <StatCard testid="stat-offers" icon={Trophy} label="Offers" value={stats?.offers ?? "—"} accent="bg-emerald-500/15 text-emerald-400" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent evaluations */}
        <div className="lg:col-span-2 bg-[#12151F] border border-slate-800/80 rounded-xl shadow-lg shadow-black/40">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800/60">
            <h3 className="font-heading font-semibold text-slate-100">Recent evaluations</h3>
            <Link to="/tracker" className="text-xs text-indigo-400 hover:text-indigo-300">View tracker →</Link>
          </div>
          <div className="divide-y divide-slate-800/50">
            {recent.length === 0 && <div className="p-6 text-sm text-slate-500">No evaluations yet.</div>}
            {recent.map((e) => (
              <div key={e.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-slate-800/30 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-200 truncate">{e.job_title}</div>
                  <div className="text-xs text-slate-500 truncate">{e.company} · {clientName(e.client_id)}</div>
                </div>
                <div className="font-mono text-sm font-bold text-slate-300 w-10 text-right">{e.fit_score}</div>
                <StatusBadge decision={e.decision} />
              </div>
            ))}
          </div>
        </div>

        {/* Clients + weekly */}
        <div className="space-y-6">
          <div className="bg-[#12151F] border border-slate-800/80 rounded-xl p-5 shadow-lg shadow-black/40">
            <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider font-semibold">
              <TrendingUp className="w-4 h-4" /> This week
            </div>
            <div className="mt-3 font-mono font-bold text-3xl text-indigo-400">{stats?.this_week ?? "—"}</div>
            <div className="text-xs text-slate-500">jobs evaluated in the last 7 days</div>
          </div>

          <div className="bg-[#12151F] border border-slate-800/80 rounded-xl shadow-lg shadow-black/40">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800/60">
              <div className="flex items-center gap-2 text-slate-100 font-semibold"><Users className="w-4 h-4 text-indigo-400" /> Active clients</div>
              <Link to="/clients" className="text-xs text-indigo-400 hover:text-indigo-300">Manage →</Link>
            </div>
            <div className="p-2">
              {clients.slice(0, 5).map((c) => {
                const pc = stats?.per_client?.[c.id];
                return (
                  <Link to={`/clients/${c.id}`} key={c.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-800/40 transition-colors">
                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 text-sm font-semibold">{c.name[0]}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-200 font-medium truncate">{c.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{c.target_summary || "No summary"}</div>
                    </div>
                    <div className="font-mono text-xs text-slate-500">{pc?.evaluated ?? 0} eval</div>
                  </Link>
                );
              })}
              {clients.length === 0 && <div className="p-4 text-sm text-slate-500">No clients yet.</div>}
            </div>
          </div>
        </div>
      </div>

      {user?.role === "executive" && (
        <div data-testid="exec-clients-breakdown" className="space-y-4">
          <div className="flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-indigo-400" />
            <h3 className="font-heading font-semibold text-slate-100 text-lg">Clients you're handling</h3>
          </div>
          <p className="text-slate-400 text-sm -mt-2">Each client with the jobs you've analysed — conclusion, fit score, status and date.</p>
          <ClientJobBreakdown clients={breakdown} loading={loadingBreakdown} />
        </div>
      )}

      {user?.role === "admin" && <ClientAccounts clients={clients} />}
    </div>
  );
}

function ClientAccounts({ clients }) {
  const [reveal, setReveal] = useState({});
  const withLogin = clients.filter((c) => c.has_login && (c.login_password_plain || c.email));

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <div data-testid="admin-client-accounts" className="space-y-4">
      <div className="flex items-center gap-2">
        <KeyRound className="w-4 h-4 text-indigo-400" />
        <h3 className="font-heading font-semibold text-slate-100 text-lg">Client login accounts</h3>
      </div>
      <p className="text-slate-400 text-sm -mt-2">Login credentials for every client portal account, including the ones your executives created.</p>

      <div className="bg-[#12151F] border border-slate-800/80 rounded-xl shadow-lg shadow-black/40 overflow-hidden">
        {withLogin.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No client login accounts yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800/60">
                  <th className="px-5 py-3 font-semibold">Client</th>
                  <th className="px-5 py-3 font-semibold">Login email</th>
                  <th className="px-5 py-3 font-semibold">Password</th>
                  <th className="px-5 py-3 font-semibold">Created by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {withLogin.map((c) => {
                  const shown = reveal[c.id];
                  const pwd = c.login_password_plain;
                  return (
                    <tr key={c.id} data-testid={`account-row-${c.id}`} className="hover:bg-slate-800/30 transition-colors">
                      <td className="px-5 py-3">
                        <Link to={`/clients/${c.id}`} className="flex items-center gap-2 text-slate-200 font-medium hover:text-indigo-300">
                          <span className="w-7 h-7 rounded-full bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center text-indigo-300 text-xs font-bold">{c.name[0]}</span>
                          {c.name}
                        </Link>
                      </td>
                      <td className="px-5 py-3 text-slate-300 font-mono text-xs">
                        <span className="inline-flex items-center gap-2">
                          {c.email || "—"}
                          {c.email && <button data-testid={`copy-email-${c.id}`} onClick={() => copy(c.email)} className="text-slate-500 hover:text-slate-200"><Copy className="w-3.5 h-3.5" /></button>}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-300 font-mono text-xs">
                        {pwd ? (
                          <span className="inline-flex items-center gap-2">
                            <span data-testid={`account-pwd-${c.id}`}>{shown ? pwd : "•".repeat(Math.min(pwd.length, 10))}</span>
                            <button data-testid={`toggle-pwd-${c.id}`} onClick={() => setReveal((r) => ({ ...r, [c.id]: !r[c.id] }))} className="text-slate-500 hover:text-slate-200">
                              {shown ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            </button>
                            <button data-testid={`copy-pwd-${c.id}`} onClick={() => copy(pwd)} className="text-slate-500 hover:text-slate-200"><Copy className="w-3.5 h-3.5" /></button>
                          </span>
                        ) : (
                          <span className="text-slate-600 italic">set before this update</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-slate-400 text-xs">
                        {c.created_by_name || "—"}
                        {c.created_by_role && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-600">({c.created_by_role})</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
